import { useOrgName } from "@src/common/reliablePartnerApi";
import { CurrentOrgLink } from "@src/MainContent";
import { useIntl } from "react-intl";
import { ContactType, useFetchContacts } from "@src/details/detailApi";
import { RPFormattedMessage } from "@src/i18n";

const Overview = () => {
  const orgName = useOrgName();
  const contacts = useFetchContacts();
  const intl = useIntl();

  return (
    <>
      <h1 className="mt-0">
        <RPFormattedMessage id="overview" defaultMessage="Yhteenveto" />
      </h1>
      <div className="py-3" />
      <h2>
        <CurrentOrgLink to="/details/contacts">
          <RPFormattedMessage id="contactInfo" defaultMessage="Yhteystiedot" />
          <i className="fa fa-pencil ml-4" />
          {contacts.isSuccess && !contacts.data.updateInfo && (
            <i
              className="fa fa-exclamation-circle ml-4 align-self-center"
              aria-hidden="true"
            ></i>
          )}
        </CurrentOrgLink>
      </h2>
      {contacts.status == "loading" && (
        <div className="page-loading-indicator" />
      )}
      {contacts.status == "success" && (
        <>
          <div className="table-responsive">
            <table className="table table-striped text-nowrap">
              <tbody>
                <tr>
                  <td>
                    <RPFormattedMessage
                      id="email"
                      defaultMessage="Sähköposti"
                    />
                  </td>
                  <td>
                    <b>
                      {contacts.data.emailType == ContactType.default &&
                        (contacts.data.default.email || (
                          <RPFormattedMessage
                            id="emailValueMissing"
                            defaultMessage="tyhjä"
                          />
                        ))}
                      {contacts.data.emailType == ContactType.custom &&
                        contacts.data.custom?.email}
                    </b>
                  </td>
                </tr>
                <tr>
                  <td>
                    <RPFormattedMessage id="phone" defaultMessage="Puhelin" />
                  </td>
                  <td>
                    <b>
                      {contacts.data.phoneType == ContactType.default &&
                        (contacts.data.default.phone || (
                          <RPFormattedMessage
                            id="phoneValueMissing"
                            defaultMessage="tyhjä"
                          />
                        ))}
                      {contacts.data.phoneType == ContactType.custom &&
                        contacts.data.custom?.phone}
                    </b>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="alert alert-secondary">
            <RPFormattedMessage
              id="contactsManageContactsText"
              defaultMessage={
                "Hallitse <b>laskutusosoitetta</b> ja muita yrityksen perustietoja <linkToCompanyAccount>yritystilillä</linkToCompanyAccount> (pääsy myös ylävalikosta yrityksen nimen alta linkistä Yrityksen tiedot)."
              }
            />
          </div>
        </>
      )}
      <div className="py-5" />
      <h2>
        <RPFormattedMessage
          id="overviewCollaborativeLaborAgreement"
          defaultMessage="Työehtosopimus (TeS)"
        />
      </h2>
      <hr />
      <table className="table">
        <tbody>
          <tr>
            <td>
              <RPFormattedMessage
                id="overviewCLAText"
                defaultMessage="Tarkista ajantasaiset tiedot raportilta. Voit tarvittaessa toimittaa meille tiedon noudattamastanne työehtosopimuksesta sähköpostitse osoitteeseen <mailToCustomerService>asiakaspalvelu@vastuugroup.fi</mailToCustomerService>."
                extraRTEProps={{
                  mailToCustomerService: {
                    subject: `${orgName}: ${intl.formatMessage({
                      id: "overviewCollaborativeLaborAgreement",
                    })}`,
                  },
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="py-5" />
      <h2>
        <RPFormattedMessage
          id="overviewEmploymentInsurance"
          defaultMessage="TyEL"
        />
      </h2>
      <hr />
      <table className="table">
        <tbody>
          <tr>
            <td>
              <RPFormattedMessage
                id="overviewEmploymentInsuranceText"
                defaultMessage="Tarkista ajantasaiset tiedot raportilta. Voit tarvittaessa toimittaa korkeintaan 10 päivää vanhan TyEL-todistuksen meille sähköpostitse osoitteeseen <mailToCustomerService>asiakaspalvelu@vastuugroup.fi</mailToCustomerService>."
                extraRTEProps={{
                  mailToCustomerService: {
                    subject: `${orgName}: ${intl.formatMessage({
                      id: "overviewEmploymentInsurance",
                    })}`,
                  },
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="py-5" />
      <h2>
        <RPFormattedMessage
          id="overviewAccidentInsurance"
          defaultMessage="Tapaturmavakuutus"
        />
      </h2>
      <hr />
      <table className="table">
        <tbody>
          <tr>
            <td>
              <RPFormattedMessage
                id="overviewAccidentInsuranceText"
                defaultMessage="Tarkista ajantasaiset tiedot raportilta. Voit tarvittaessa toimittaa korkeintaan 10 päivää vanhan tapaturmavakuutustodistuksen meille <linkToAccidentInsuranceForm>tällä lomakkeella</linkToAccidentInsuranceForm>."
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="py-5" />
      <h2>
        <RPFormattedMessage
          id="overviewHealthInsurance"
          defaultMessage="Työterveyshuolto"
        />
      </h2>
      <hr />
      <table className="table">
        <tbody>
          <tr>
            <td>
              <RPFormattedMessage
                id="overviewHealthInsuranceText"
                defaultMessage="Tarkista ajantasaiset tiedot raportilta. Voit tarvittaessa toimittaa tiedon työterveyssopimuksestanne meille sähköpostitse osoitteeseen <mailToCustomerService>asiakaspalvelu@vastuugroup.fi</mailToCustomerService>."
                extraRTEProps={{
                  mailToCustomerService: {
                    subject: `${orgName}: ${intl.formatMessage({
                      id: "overviewHealthInsurance",
                    })}`,
                  },
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="py-5" />
      <h2>
        <RPFormattedMessage
          id="overviewTaxInformation"
          defaultMessage="Verotiedot"
        />
      </h2>
      <hr />
      <table className="table">
        <tbody>
          <tr>
            <td>
              <RPFormattedMessage
                id="overviewTaxInsuranceText"
                defaultMessage="Tarkista ajantasaiset tiedot raportilta. Voit tarvittaessa toimittaa korkeintaan 10 päivää vanhan verotustodistuksen meille sähköpostitse osoitteeseen <mailToCustomerService>asiakaspalvelu@vastuugroup.fi</mailToCustomerService>."
                extraRTEProps={{
                  mailToCustomerService: {
                    subject: `${orgName}: ${intl.formatMessage({
                      id: "overviewTaxInformation",
                    })}`,
                  },
                }}
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="py-5" />
      <h2>
        <RPFormattedMessage
          id="overviewLiabilityInsurance"
          defaultMessage="Vastuuvakuutus"
        />
      </h2>
      <hr />
      <table className="table">
        <tbody>
          <tr>
            <td>
              <RPFormattedMessage
                id="overviewLiabilityInsuranceText"
                defaultMessage="Tarkista ajantasaiset tiedot raportilta. Voit tarvittaessa toimittaa voimassaolevan vastuuvakuutustodistuksen meille <linkToLiabilityInsuranceForm>tällä lomakkeella</linkToLiabilityInsuranceForm>."
              />
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

export default Overview;
